import React from 'react';
import { useOutletContext } from 'react-router';
import { LATE_REG_CUTOFF_ROUND } from 'constants/play';
import type { TournamentState } from 'types/interfaces/play';
import PlayNotFound from '../../PlayNotFound';
import { PlayLayout } from '../../layout/PlayLayout';
import type { TournamentOutletContext } from '../../PlayRoutes';
import { useTournament } from '../../hooks/useTournament';
import InvitePage from '../Invite/InvitePage';
import NameStepPage from '../Invite/NameStepPage';
import HostLobbyPage from '../Host/HostLobbyPage';
import PlayerHomePage from '../Player/PlayerHomePage';

/** Decision 17: joining stays open until round 2 starts when late registration is on. */
export const registrationClosed = (state: TournamentState): boolean => {
  const { tournament, rounds } = state;
  if (typeof tournament.registrationOpen === 'boolean') return !tournament.registrationOpen;
  if (tournament.status === 'COMPLETE') return true;
  if (tournament.status === 'LOBBY') return false;
  if (!tournament.lateRegistration) return true;
  return rounds.some((r) => r.number >= LATE_REG_CUTOFF_ROUND && r.startedAt !== null);
};

export const TournamentLoading: React.FC = () => (
  <PlayLayout>
    <div className="play-card" aria-busy="true" data-testid="play-loading">
      <p className="play-helper">Loading…</p>
    </div>
  </PlayLayout>
);

/**
 * /play/:code is role-aware (route table in the plan): a visitor sees the
 * invite page, a signed-in non-member the name step, a PLAYER the player home,
 * and the TO the lobby or round control.
 */
const TournamentHomePage: React.FC = () => {
  const { code } = useOutletContext<TournamentOutletContext>();
  const { state, isLoading, notFound, isAuthenticated } = useTournament(code);

  if (notFound) return <PlayNotFound reason="missing" />;
  if (isLoading || !state) return <TournamentLoading />;

  const closed = registrationClosed(state);
  if (state.viewer.role === 'TO') return <HostLobbyPage code={code} state={state} />;
  if (state.viewer.role === 'PLAYER') return <PlayerHomePage code={code} state={state} />;
  if (!isAuthenticated || closed) return <InvitePage code={code} tournament={state.tournament} closed={closed} />;
  return <NameStepPage code={code} tournament={state.tournament} prefill={state.viewer.prefill} />;
};

export default TournamentHomePage;
